import { createLogger } from '@svenskaspelab/nova-core/log';

import {
  BadRequestFileUploadError,
  type ICreateExternalWagerUseCase,
  type IDefinitionService,
  type IDrawService,
  type IExternalWagerService,
  InternalError,
} from '../../../domain/entry.js';

const logger = createLogger('poolbetting.file-upload');

/**
 * Use case for uploading a external wager file.
 *
 * @param externalWagerService External wager service dependency
 * @param drawService Draw service dependency
 * @param definitionService Definition service dependency
 * @returns Use case executor for getting wagers
 */
export function createExternalWagerUseCase(
  externalWagerService: IExternalWagerService,
  drawService: IDrawService,
  definitionService: IDefinitionService
): ICreateExternalWagerUseCase {
  return {
    execute: async ({ betPropertyData, systemData }): ReturnType<ICreateExternalWagerUseCase['execute']> => {
      const definition = await definitionService.resolveDefinitionAsync(betPropertyData.product);
      if (!definition) {
        logger.info(`Unable to load definition for product: ${betPropertyData.product}`);
        throw new InternalError(`Unable to load definition for product: ${betPropertyData.product}`);
      }

      if (definition.betAmounts.length > 1 && betPropertyData.betAmount === undefined) {
        logger.info(
          'Bet amount is required for products that support multiple bet amounts, but it was not provided in the file'
        );
        // This might not be valid for Maltipset to throw
        throw new BadRequestFileUploadError(
          'Bet amount is required for products that support multiple bet amounts, but it was not provided in the file',
          undefined,
          'CONTENT_BET_AMOUNT_MISSING'
        );
      }

      // Product that is jackpot products can only have one playable opened draw at a time (like Stryktipset and Europatipset),
      // therefore draw number isn't needed.
      // For scoreboard products like Bomben either bomben number (kept for legacy reasons) or draw number must be given
      if (!definition.hasJackpot && !(betPropertyData.drawNumber || betPropertyData.bombenNumber)) {
        logger.info('Bomben number or draw number must be given for product result products');
        throw new BadRequestFileUploadError(
          'Bomben number or draw number must be given for product result products',
          undefined,
          'CONTENT_DRAW_NUMBER_MISSING'
        );
      }

      // Assert that draw is in correct state before saving the file
      if (betPropertyData.drawNumber) {
        let draw: Awaited<ReturnType<IDrawService['getDrawAsync']>>;

        try {
          draw = await drawService.getDrawAsync(betPropertyData.product, {
            drawNumber: betPropertyData.drawNumber,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown draw fetch error';
          logger.error(`Failed to fetch draw ${betPropertyData.drawNumber}: ${errorMessage}`);
          throw new InternalError(`Failed to fetch draw ${betPropertyData.drawNumber}`, { cause: error });
        }

        if (!draw) {
          logger.info(`No draw exists: ${betPropertyData.drawNumber}`);
          throw new BadRequestFileUploadError(
            `No draw exists: ${betPropertyData.drawNumber}`,
            undefined,
            'DRAW_CLOSED'
          );
        }

        if (draw.draw.drawState.client !== 'Opened') {
          logger.info(`Draw state must be Opened, actual: ${draw.draw.drawState.client}`);
          throw new BadRequestFileUploadError(
            `Draw state must be Opened, actual: ${draw.draw.drawState.client}`,
            undefined,
            'DRAW_CLOSED'
          );
        }
      }

      const response = await externalWagerService.createExternalWagerAsync(betPropertyData.product, {
        betAmount: betPropertyData.betAmount,
        drawNumber: betPropertyData.drawNumber,
        product: betPropertyData.product,
        systemType: systemData.systemType,
        items: systemData.items,
      });

      return response;
    },
  };
}
